import { useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, Upload } from 'lucide-react';
import { useRef, useState, type ReactNode } from 'react';
import { Button } from '../../../components/Button';
import { JOINING_DOCUMENT_TONES } from '../../../components/status-tones';
import { ToneChip } from '../../../components/Tone';
import { errorMessage } from '../../../lib/api-client';
import { candidateDocumentsKey, uploadCandidateDocument, useCandidateDocuments } from '../api/hires-api';
import styles from './CandidateDocuments.module.css';

/**
 * S24 Q1 — "Documents we need" on the candidate's own offer page, once they have accepted.
 * Built for a phone at 375px: one line per document, one tap to upload a photo or a PDF. A
 * rejected one says what to fix (§L11). No account — the link is the key (D16).
 */
export function CandidateDocuments({ token }: { readonly token: string }): ReactNode {
  const query = useCandidateDocuments(token, true);
  const queryClient = useQueryClient();
  const [pendingKey, setPendingKey] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const view = query.data;
  if (!view || view.documents.length === 0) return null;
  const isDone = view.documents.filter((line) => line.isRequired).every((line) => line.state !== 'missing' && line.state !== 'rejected' && line.state !== 'expired');

  async function upload(key: string, file: File): Promise<void> {
    setPendingKey(key);
    setProblem(null);
    try {
      queryClient.setQueryData(candidateDocumentsKey(token), await uploadCandidateDocument(token, key, file, null));
    } catch (error) {
      setProblem(errorMessage(error));
    } finally {
      setPendingKey(null);
    }
  }

  return (
    <section className={styles.section} aria-labelledby="documents-heading">
      <h2 id="documents-heading" className={styles.heading}>
        Documents we need
      </h2>
      <p className={styles.lead}>{isDone ? 'Thank you — we have everything we need from you for now.' : 'Please send these before your first day. A clear photo from your phone is fine — PDF, JPG or PNG, up to 5 MB each.'}</p>
      {problem && <p className={styles.warning}>{problem}</p>}
      <ul className={styles.list}>
        {view.documents.map((line) => (
          <li key={line.key} className={styles.item}>
            <div className={styles.itemHead}>
              <span className={styles.name}>
                {line.label}
                {!line.isRequired && <span className={styles.optional}> (if you have it)</span>}
              </span>
              <ToneChip tone={JOINING_DOCUMENT_TONES[line.state]}>{line.stateLabel}</ToneChip>
            </div>
            {line.fileName && line.state !== 'missing' && <span className={styles.file}>{line.fileName}</span>}
            {line.rejectionReason && <p className={styles.reason}>{line.rejectionReason}</p>}
            {view.canUpload && line.state !== 'verified' && <UploadControl label={line.label} hasFile={line.state !== 'missing'} isPending={pendingKey === line.key} onPick={(file) => void upload(line.key, file)} />}
            {line.state === 'verified' && (
              <span className={styles.checked}>
                <CheckCircle2 size={14} aria-hidden /> Checked
              </span>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}

function UploadControl({ label, hasFile, isPending, onPick }: { readonly label: string; readonly hasFile: boolean; readonly isPending: boolean; readonly onPick: (file: File) => void }): ReactNode {
  const input = useRef<HTMLInputElement | null>(null);
  return (
    <>
      <input
        ref={input}
        className={styles.hiddenFile}
        type="file"
        accept=".pdf,.jpg,.jpeg,.png,image/*"
        aria-label={`Choose your ${label}`}
        onChange={(event) => {
          const file = event.target.files?.[0];
          event.target.value = '';
          if (file) onPick(file);
        }}
      />
      <Button variant={hasFile ? 'outline' : 'solid'} size="lg" block icon={Upload} isPending={isPending} onClick={() => input.current?.click()}>
        {hasFile ? 'Send a new one' : 'Upload'}
      </Button>
    </>
  );
}
